import React, { useState, useEffect } from 'react';
import { Container, Table, Badge, Button, Card, Spinner, Form, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faCheckCircle, faEdit } from '@fortawesome/free-solid-svg-icons';
import MainLayout from '../../layouts/MainLayout';

const StockDiproses = () => {
  const [loading, setLoading] = useState(true);
  const [processingRequests, setProcessingRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDebugTools, setShowDebugTools] = useState(false);
  const [loadingState, setLoadingState] = useState({});
  const [username, setUsername] = useState('');

  useEffect(() => {
    fetchProcessingRequests();
    
    // Ambil username dari localStorage atau JWT
    const storedUsername = localStorage.getItem('username');
    setUsername(storedUsername || 'unknown');
  }, []);

  const fetchProcessingRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      if (!token) {
        toast.error('Sesi login telah berakhir');
        return;
      }

      console.log('Fetching all inventory requests...');
      
      const response = await axios.get('https://rumahakrilik.id/api/inventory-requests/', {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Response from all requests:', response.data);

      const allData = Array.isArray(response.data) ? response.data : 
                     (response.data.results || []);
                     
      // Filter untuk hanya menampilkan status 'approved'
      const approvedData = allData.filter(item => item.status === 'approved');
      console.log('Filtered approved data:', approvedData);
      
      setProcessingRequests(approvedData);
    } catch (error) {
      console.error('Error fetching processing requests:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Status code:', error.response.status);
      }
      toast.error('Gagal memuat data permintaan stock yang diproses');
    } finally {
      setLoading(false);
    }
  };

  const handleReceive = async (requestId) => {
    try {
      setLoadingState(prevState => ({ ...prevState, [requestId]: true }));
      const token = localStorage.getItem('jwtToken');
      console.log(`Attempting to receive stock for request ID: ${requestId}`);
      
      // Use the correct endpoint that worked successfully
      const response = await axios.post(`https://rumahakrilik.id/api/inventory-requests/${requestId}/receive/`, {
        received_by: username,
        received_date: new Date().toISOString(),
        update_inventory: true // Tambahkan parameter ini
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Response from receive:', response.data);
      
      // Tampilkan informasi perubahan stock jika tersedia dalam respons
      if (response.data.data) {
        const { item_name, quantity, previous_stock, new_stock } = response.data.data;
        toast.success(
          `Stock berhasil diterima!` + 
          `\nItem: ${item_name}` + 
          `\nJumlah: +${quantity}` + 
          `\nStock sebelumnya: ${previous_stock}` + 
          `\nStock baru: ${new_stock}` +
          `\nDiterima oleh: ${username}`
        );
        
        // Lakukan sinkronisasi inventory setelah menerima stock
        await triggerInventorySync(token);
      } else {
        toast.success(`Stock berhasil diterima oleh ${username}!`);
      }
      
      // Hapus item dari state lokal tanpa perlu fetch ulang
      setProcessingRequests(prevRequests => 
        prevRequests.filter(request => request.id !== requestId)
      );
      
      // Fetch ulang data setelah timeout lebih lama
      setTimeout(() => {
        fetchProcessingRequests();
      }, 1500);
    } catch (error) {
      console.error('Error receiving stock:', error);
      
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      } else if (error.request) {
        console.error('Error request:', error.request);
      } else {
        console.error('Error message:', error.message);
      }
      
      // Coba endpoint yang berhasil pada klik kedua
      const success = await testEndpoints(requestId);
      if (!success) {
        toast.error(`Gagal menerima stock: ${error.response?.data?.message || error.message || 'Unknown error'}`);
      }
    } finally {
      setLoadingState(prevState => ({ ...prevState, [requestId]: false }));
    }
  };


  const triggerInventorySync = async (token) => {
    try {
      toast.info("Melakukan sinkronisasi inventory...");
      
      // Gunakan satu endpoint yang konsisten di semua kode
      const primaryEndpoint = '/api/inventory-sync/';
      
      try {
        const response = await axios.post(`https://rumahakrilik.id${primaryEndpoint}`, {}, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (response.status === 200) {
          const data = response.data;
          const updateCount = data.updated_count || 0;
          toast.success(`Sinkronisasi inventory berhasil, ${updateCount} item diperbarui`);
          return true;
        }
      } catch (error) {
        console.error(`Gagal dengan endpoint utama ${primaryEndpoint}:`, error);
        
        // Coba endpoint yang berbeda secara berurutan sebagai fallback
        const fallbackEndpoints = [
          '/api/inventory/sync/',
          '/api/sync-inventory/',
          '/debug-sync/'
        ];
        
        for (const endpoint of fallbackEndpoints) {
          try {
            console.log(`Trying fallback endpoint: ${endpoint}`);
            const response = await axios.post(`https://rumahakrilik.id${endpoint}`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.status === 200) {
              toast.success(`Sinkronisasi berhasil dengan endpoint alternatif: ${endpoint}`);
              return true;
            }
          } catch (err) {
            console.log(`Gagal dengan ${endpoint}:`, err);
          }
        }
      }
      
      // Jika semua gagal
      toast.warning("Sinkronisasi inventory gagal, mencoba dengan endpoint emergency...");
      
      try {
        // Terakhir, coba gunakan endpoint debug sebagai fallback terakhir
        const debugResponse = await axios.post(`https://rumahakrilik.id/debug-sync/`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (debugResponse.status === 200) {
          toast.success("Emergency sync berhasil!");
          return true;
        }
      } catch (debugError) {
        console.error("Emergency sync juga gagal:", debugError);
      }
      
      toast.error("Semua upaya sinkronisasi gagal, silakan coba lagi nanti");
      return false;
    } catch (error) {
      console.error("Error triggering inventory sync:", error);
      toast.error(`Error sinkronisasi: ${error.message}`);
      return false;
    }
  };

  const handleQuantityCorrection = async (requestId, actualQuantity) => {
    try {
      const token = localStorage.getItem('jwtToken');
      console.log(`Attempting to correct quantity for request ID: ${requestId} to ${actualQuantity}`);
      
      // Use the endpoint that worked successfully
      const response = await axios.post(`https://rumahakrilik.id/api/inventory-requests/${requestId}/receive/`, {
        actual_quantity: actualQuantity
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Response from quantity correction:', response.data);
      
      // Tampilkan informasi perubahan stock jika tersedia dalam respons
      if (response.data.data) {
        const { item_name, quantity, previous_stock, new_stock } = response.data.data;
        toast.success(
          `Stock berhasil diterima dengan koreksi jumlah!` + 
          `\nItem: ${item_name}` + 
          `\nJumlah koreksi: ${quantity}` + 
          `\nStock sebelumnya: ${previous_stock}` + 
          `\nStock baru: ${new_stock}`
        );
      } else {
        toast.success('Stock berhasil diterima dengan koreksi jumlah!');
      }
      
      // Hapus item dari state lokal tanpa perlu fetch ulang
      setProcessingRequests(prevRequests => 
        prevRequests.filter(request => request.id !== requestId)
      );
      
      // Fetch ulang data setelah timeout singkat
      setTimeout(() => {
        fetchProcessingRequests();
      }, 500);
    } catch (error) {
      console.error('Error receiving stock with correction:', error);
      
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      } else if (error.request) {
        console.error('Error request:', error.request);
      } else {
        console.error('Error message:', error.message);
      }
      
      // Coba endpoint alternatif
      const success = await testEndpoints(requestId);
      if (!success) {
        toast.error(`Gagal menerima stock dengan koreksi: ${error.response?.data?.message || error.message || 'Unknown error'}`);
      }
    }
  };

  const testEndpoints = async (requestId) => {
    const token = localStorage.getItem('jwtToken');
    
    // Urutkan endpoint prioritas yang berhasil pada klik kedua
    const endpoints = [
      // Endpoint yang sukses berdasarkan notifikasi "Berhasil dengan endpoint: /api/inventory-requests/{id}/receive/"
      `/api/inventory-requests/${requestId}/receive/`,
      
      // Endpoint lainnya sebagai backup
      `/api/inventory-requests/${requestId}/approve/`,
      `/api/inventory/requests/${requestId}/approve/`,
      `/api/inventory-requests/approve/${requestId}/`,
      `/api/inventory-requests/${requestId}/complete/`,
      `/api/inventory-requests/${requestId}/update-status/`,
      `/api/inventory/receive/${requestId}/`,
    ];
    
    for (const endpoint of endpoints) {
      try {
        console.log(`Trying endpoint: ${endpoint}`);
        const response = await axios.post(`https://rumahakrilik.id${endpoint}`, 
          { status: 'received' }, 
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log(`Success with endpoint ${endpoint}:`, response);
        toast.success(`Berhasil dengan endpoint: ${endpoint}`);
        
        // Hapus item dari list setelah endpoint berhasil
        setProcessingRequests(prevRequests => 
          prevRequests.filter(request => request.id !== requestId)
        );
        
        // Refresh data setelah berhasil
        setTimeout(() => {
          fetchProcessingRequests();
        }, 500);
        
        return true;
      } catch (error) {
        console.error(`Failed with endpoint ${endpoint}:`, error.response || error);
      }
    }
    
    toast.error('Semua endpoint yang dicoba gagal');
    return false;
  };

  const filteredRequests = processingRequests.filter(request =>
    request.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.requested_by?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.notes?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <MainLayout>
      <Container fluid className="py-4">
        <h1 className="mb-4">Stock Diproses</h1>
        
        <Card className="mb-4">
          <Card.Header className="bg-info text-white">
            <h5 className="mb-0">Permintaan Stock yang Sedang Diproses</h5>
          </Card.Header>
          <Card.Body>
            <InputGroup className="mb-3" style={{ maxWidth: '400px' }}>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Cari berdasarkan nama, pemohon, catatan..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Memuat data...</p>
              </div>
            ) : (
              <>
                {filteredRequests.length === 0 ? (
                  <div className="text-center py-5">
                    <p>Tidak ada permintaan stock yang sedang diproses</p>
                  </div>
                ) : (
                  <Table responsive striped bordered hover>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Nama Barang</th>
                        <th>Jumlah</th>
                        <th>Pemohon</th>
                        <th>Tanggal Permintaan</th>
                        <th>Tanggal Persetujuan</th>
                        <th>Status</th>
                        <th>Catatan</th>
                        <th>Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRequests.map((request) => (
                        <tr key={request.id}>
                          <td>{request.id}</td>
                          <td>{request.item_name}</td>
                          <td>{request.quantity}</td>
                          <td>{request.requested_by}</td>
                          <td>{new Date(request.request_date).toLocaleDateString('id-ID')}</td>
                          <td>{request.approved_date ? new Date(request.approved_date).toLocaleDateString('id-ID') : '-'}</td>
                          <td>
                            <Badge bg="info">Diproses</Badge>
                          </td>
                          <td>{request.notes || '-'}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button 
                                variant="success" 
                                size="sm"
                                onClick={() => {
                                  // Tambahkan notifikasi loading
                                  toast.info('Memproses permintaan...', { autoClose: 1000 });
                                  handleReceive(request.id);
                                }}
                                disabled={loadingState[request.id]}
                                title="Terima barang sesuai jumlah yang diminta"
                              >
                                {loadingState[request.id] ? (
                                  <><Spinner size="sm" animation="border" /> Memproses...</>
                                ) : (
                                  <><FontAwesomeIcon icon={faCheckCircle} /> Terima</>
                                )}
                              </Button>
                              <Button 
                                variant="warning" 
                                size="sm"
                                onClick={() => {
                                  const actualQuantity = prompt(
                                    `Masukkan jumlah aktual untuk ${request.item_name}:`,
                                    request.quantity
                                  );
                                  if (actualQuantity !== null) {
                                    const qty = parseInt(actualQuantity);
                                    if (!isNaN(qty) && qty >= 0) {
                                      handleQuantityCorrection(request.id, qty);
                                    } else {
                                      toast.error('Jumlah tidak valid');
                                    }
                                  }
                                }}
                                title="Terima barang dengan jumlah yang berbeda"
                              >
                                <FontAwesomeIcon icon={faEdit} /> Koreksi
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </>
            )}
          </Card.Body>
        </Card>

        {showDebugTools && (
          <Card className="mt-4">
            <Card.Header className="bg-warning">Debug Tools</Card.Header>
            <Card.Body>
              <div className="d-flex gap-2">
                {filteredRequests.map(request => (
                  <div key={request.id} className="border p-2">
                    <p>ID: {request.id} - {request.item_name}</p>
                    <div className="d-flex flex-wrap gap-2">
                      <Button 
                        size="sm" 
                        variant="outline-secondary"
                        onClick={() => testEndpoints(request.id)}
                      >
                        Test Endpoints
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        <div className="d-flex justify-content-between mt-3">
          <Button 
            variant="outline-dark" 
            size="sm"
            onClick={() => setShowDebugTools(!showDebugTools)}
          >
            {showDebugTools ? 'Hide Debug Tools' : 'Show Debug Tools'}
          </Button>
          <Button 
            variant="primary" 
            onClick={fetchProcessingRequests}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="ms-2">Memuat...</span>
              </>
            ) : 'Refresh Data'}
          </Button>
        </div>
      </Container>
    </MainLayout>
  );
};

export default StockDiproses;